// Daily 2-hour learning plan tracker.
// Goal per day: 60 min WRITTEN (morning) + 60 min ORAL (evening) = 120 min total.
// Tracked in localStorage by date (YYYY-MM-DD).

export const WRITTEN_TARGET_MIN = 60;
export const ORAL_TARGET_MIN = 60;
export const TOTAL_TARGET_MIN = WRITTEN_TARGET_MIN + ORAL_TARGET_MIN;

const KEY_PREFIX = "dailyPlan_";

function todayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${KEY_PREFIX}${y}-${m}-${day}`;
}

function emptyPlan() {
  return {
    writtenMinutes: 0,
    oralMinutes: 0,
    writtenSessions: [], // [{ type, ts }]
    oralSessions: [],
    lessonCompleted: false,
    completedAt: null,
  };
}

function readPlan(date) {
  const key = date ? `${KEY_PREFIX}${date}` : todayKey();
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...emptyPlan(), ...JSON.parse(raw) } : emptyPlan();
  } catch {
    return emptyPlan();
  }
}

function writePlan(plan) {
  try {
    localStorage.setItem(todayKey(), JSON.stringify(plan));
  } catch {}
}

export function getTodayPlan() {
  return readPlan();
}

export function addWrittenMinutes(minutes, sourceType = "lesson") {
  if (!minutes || minutes <= 0) return;
  const plan = readPlan();
  plan.writtenMinutes = Math.min(WRITTEN_TARGET_MIN, plan.writtenMinutes + minutes);
  plan.writtenSessions.push({ type: sourceType, minutes, ts: Date.now() });
  checkComplete(plan);
  writePlan(plan);
  return plan;
}

export function addOralMinutes(minutes, sourceType = "speak") {
  if (!minutes || minutes <= 0) return;
  const plan = readPlan();
  plan.oralMinutes = Math.min(ORAL_TARGET_MIN, plan.oralMinutes + minutes);
  plan.oralSessions.push({ type: sourceType, minutes, ts: Date.now() });
  checkComplete(plan);
  writePlan(plan);
  return plan;
}

export function markLessonCompleted() {
  const plan = readPlan();
  plan.lessonCompleted = true;
  checkComplete(plan);
  writePlan(plan);
  return plan;
}

function checkComplete(plan) {
  if (
    !plan.completedAt &&
    plan.writtenMinutes >= WRITTEN_TARGET_MIN &&
    plan.oralMinutes >= ORAL_TARGET_MIN
  ) {
    plan.completedAt = Date.now();
    // Notify gamification for perfect-day badges + streak. Lazy import avoids
    // a hard cycle since gamification doesn't need dailyPlan.
    try {
      // eslint-disable-next-line global-require
      const { recordPerfectDay } = require("../gamification");
      recordPerfectDay();
    } catch {}
  }
}

export function getProgress() {
  const plan = readPlan();
  const writtenPct = Math.round((plan.writtenMinutes / WRITTEN_TARGET_MIN) * 100);
  const oralPct = Math.round((plan.oralMinutes / ORAL_TARGET_MIN) * 100);
  const totalPct = Math.round(
    ((plan.writtenMinutes + plan.oralMinutes) / TOTAL_TARGET_MIN) * 100
  );
  return {
    plan,
    writtenPct,
    oralPct,
    totalPct,
    writtenDone: plan.writtenMinutes >= WRITTEN_TARGET_MIN,
    oralDone: plan.oralMinutes >= ORAL_TARGET_MIN,
    allDone: !!plan.completedAt,
  };
}

// Compute current "perfect day" streak — consecutive days where BOTH written
// and oral targets were hit. Walks backward from today.
export function getPerfectStreak() {
  let streak = 0;
  for (let i = 0; i < 365; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const plan = readPlan(`${y}-${m}-${day}`);
    const both = plan.writtenMinutes >= WRITTEN_TARGET_MIN && plan.oralMinutes >= ORAL_TARGET_MIN;
    if (both) streak++;
    else if (i === 0) continue; // today might not be done yet — don't count as missed
    else break;
  }
  return streak;
}

// How many days in the last 7 where BOTH halves were completed.
export function getWeekStats() {
  const days = getLast7Days();
  const perfect = days.filter((d) => d.complete).length;
  const partial = days.filter((d) => !d.complete && d.total > 0).length;
  const missed = days.filter((d) => d.total === 0).length;
  return { perfect, partial, missed, days };
}

// Get last 7 days for streak/chart visualization
export function getLast7Days() {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const dateStr = `${y}-${m}-${day}`;
    const plan = readPlan(dateStr);
    days.push({
      date: dateStr,
      label: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()],
      writtenMinutes: plan.writtenMinutes,
      oralMinutes: plan.oralMinutes,
      total: plan.writtenMinutes + plan.oralMinutes,
      complete: !!plan.completedAt,
    });
  }
  return days;
}

// Get an entire month's data (grid-friendly with leading/trailing blanks).
// year & monthIdx (0-based, e.g. April = 3).
export function getMonthData(year, monthIdx) {
  const first = new Date(year, monthIdx, 1);
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const startWeekday = first.getDay(); // 0 = Sun

  const cells = [];
  // Leading blanks (so first day lines up with weekday)
  for (let i = 0; i < startWeekday; i++) cells.push({ blank: true });

  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, monthIdx, d);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${y}-${m}-${day}`;
    const plan = readPlan(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isFuture = date > today;
    const isToday = date.toDateString() === today.toDateString();
    cells.push({
      date: dateStr,
      day: d,
      writtenMinutes: plan.writtenMinutes,
      oralMinutes: plan.oralMinutes,
      writtenDone: plan.writtenMinutes >= WRITTEN_TARGET_MIN,
      oralDone: plan.oralMinutes >= ORAL_TARGET_MIN,
      complete: !!plan.completedAt,
      isToday,
      isFuture,
      blank: false,
    });
  }
  // Trailing blanks to complete last week row
  while (cells.length % 7 !== 0) cells.push({ blank: true });

  // Month-level summary (only for non-future days)
  const past = cells.filter((c) => !c.blank && !c.isFuture);
  const summary = {
    perfect: past.filter((c) => c.complete).length,
    partial: past.filter((c) => !c.complete && (c.writtenMinutes > 0 || c.oralMinutes > 0)).length,
    missed: past.filter((c) => c.writtenMinutes === 0 && c.oralMinutes === 0).length,
    totalDays: past.length,
    monthName: first.toLocaleString("default", { month: "long" }),
    year,
  };

  return { cells, summary };
}

// Hook helper: track time-on-page automatically.
// Call once per minute while user is on a written/oral page.
let activeTickInterval = null;

export function startActivityTracking(kind, sourceType) {
  // Increments minutes by 1 every 60 seconds while page is active.
  stopActivityTracking();
  let lastTick = Date.now();
  activeTickInterval = setInterval(() => {
    if (document.hidden) return;
    const now = Date.now();
    const elapsed = (now - lastTick) / 60000;
    lastTick = now;
    if (elapsed > 0 && elapsed < 5) {
      // Round to nearest minute, ignore long gaps (probably afk)
      if (kind === "written") addWrittenMinutes(elapsed, sourceType);
      else if (kind === "oral") addOralMinutes(elapsed, sourceType);
    }
  }, 60000);
  return stopActivityTracking;
}

export function stopActivityTracking() {
  if (activeTickInterval) {
    clearInterval(activeTickInterval);
    activeTickInterval = null;
  }
}
